import { useCms } from '~/components/hooks/useCms';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import { reportErrorInProductionOrThrow } from '~/Utils';

type OrganizationContext = 'sysconfig' | 'client';
type OrganizationContextKind = OrganizationContext | 'sysconfig_with_client_fallback';

interface UseOrganizationDetailsOptions {
  kind: OrganizationContextKind;
}

interface UseOrganizationDetailsResult {
  organization: OrganizationModel | null;
  contextUsed: OrganizationContext | null;
  requestedContext: OrganizationContextKind;
  isError: boolean;
  errorMessage: string;
  hasWarning: boolean;
  warningMessage: string;
}

interface OrganizationSource {
  organization?: OrganizationModel;
  context: OrganizationContext;
  shouldWarn?: boolean;
}

/**
 * A dual-context aware hook that provides organization details.
 *
 * This hook determines which organization context to use based on the provided kind:
 * - 'sysconfig': Try sysconfig org first, fallback to cms org with warning
 * - 'client': Try cms org first, fallback to sysconfig org with warning
 * - 'sysconfig_with_client_fallback': Prefer sysconfig org, fallback to cms org (no warning)
 *
 * @param {UseOrganizationDetailsOptions} options - Configuration options
 * @returns {UseOrganizationDetailsResult} Organization details with consistent interface
 */
export const useOrganizationDetails = ({ kind }: UseOrganizationDetailsOptions): UseOrganizationDetailsResult => {
  const cmsData = useCms();
  const sysconfigData = useSysconfig() as { organization?: OrganizationModel };

  // Helper function to create warning messages
  const createFallbackWarning = (requested: OrganizationContextKind, actual: OrganizationContext): string => {
    const componentType = requested === 'sysconfig' ? 'SystemConfiguration' : 'regular CMS';
    return `useOrganizationDetails: Requested ${requested} context but fell back to ${actual} context. This may indicate incorrect usage in a ${componentType} component.`;
  };

  const createResult = (
    organization: OrganizationModel | null,
    contextUsed: OrganizationContext | null,
    isError: boolean = false,
    errorMessage: string = '',
    hasWarning: boolean = false,
    warningMessage: string = ''
  ): UseOrganizationDetailsResult => ({
    organization,
    contextUsed,
    requestedContext: kind,
    isError,
    errorMessage,
    hasWarning,
    warningMessage,
  });

  const tryOrganizationSources = (sources: OrganizationSource[]): UseOrganizationDetailsResult => {
    for (const source of sources) {
      if (source.organization) {
        const warningMessage = source.shouldWarn ? createFallbackWarning(kind, source.context) : '';

        if (source.shouldWarn) {
          reportErrorInProductionOrThrow(new Error(warningMessage));
        }

        return createResult(source.organization, source.context, false, '', source.shouldWarn || false, warningMessage);
      }
    }

    // No organization found in any source
    return createResult(
      null,
      null,
      true,
      'useOrganizationDetails: No organization available in either sysconfig or client context'
    );
  };

  const sourceConfigurations: Record<OrganizationContextKind, OrganizationSource[]> = {
    sysconfig: [
      { organization: sysconfigData?.organization, context: 'sysconfig' },
      { organization: cmsData?.userOrganization, context: 'client', shouldWarn: true },
    ],
    client: [
      { organization: cmsData?.userOrganization, context: 'client' },
      { organization: sysconfigData?.organization, context: 'sysconfig', shouldWarn: true },
    ],
    sysconfig_with_client_fallback: [
      { organization: sysconfigData?.organization, context: 'sysconfig' },
      { organization: cmsData?.userOrganization, context: 'client' },
    ],
  };

  const sources = sourceConfigurations[kind];
  if (!sources) {
    return createResult(
      null,
      null,
      true,
      `useOrganizationDetails: Invalid kind "${kind}". Must be 'sysconfig', 'client', or 'sysconfig_with_client_fallback'`
    );
  }

  return tryOrganizationSources(sources);
};

export default useOrganizationDetails;
